import {useCallback, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {getWhatsAppPlainTextLength, WHATSAPP_MAX_TEXT_LENGTH} from '../../../pages/branding/whatsAppScreen/whatsAppValidation';
import {bounceMessage} from '../../../layout/userMessages/UserMessage';
import setGlobalState from '../../../redux/setGlobalState';
import {TState} from '../../../redux/store.type';
import {ButtonTemplateItem, TemplateStructure} from '../types/templateStructure';
import {calculateTotalCharacters, getFilledItemsCount, getTotalEditableItemsCount} from '../utils/templateStatistics';
import {hasDisabledVariables, hasDisabledVariablesInFreeMessage} from '../utils/variableUtils';

type DisabledVariablesMap = Parameters<typeof hasDisabledVariables>[1];
import {serializeTemplateStructure} from '../utils/formatUtils';

interface UseWhatsAppEditorModeParams {
    freeMessage: string;
    structure: TemplateStructure;
    disabledVariables: DisabledVariablesMap;
    hasTemplateChanged: boolean;
    tWhatsapp: (key: string) => string;
    modeKey?: string;
}

export function useWhatsAppEditorMode({
    freeMessage,
    structure,
    disabledVariables,
    hasTemplateChanged,
    tWhatsapp,
    modeKey,
}: UseWhatsAppEditorModeParams) {
    const key = modeKey ?? 'default';
    const persistedMode = useSelector((s: TState) => {
        return s.ui.whatsappEditorMode?.[key];
    });
    const segmentedValue = useMemo(() => persistedMode ?? 'template', [persistedMode]);

    useEffect(() => {
        if(persistedMode == null) {
            setGlobalState(`ui.whatsappEditorMode.${key}`, 'template');
        }
    }, [key, persistedMode]);

    const updateSegmentedValue = useCallback((next: string) => {
        setGlobalState(`ui.whatsappEditorMode.${key}`, next);
    }, [key]);

    const hasFreeChanged = useMemo(
        () => freeMessage !== serializeTemplateStructure(structure),
        [freeMessage, structure],
    );

    const handleSegmentedChange = useCallback(
        (next: string) => {
            if(persistedMode === 'free' && next === 'template' && hasFreeChanged) {
                bounceMessage(
                    tWhatsapp('freeToTemplateUnsavedAttention'),
                    'attention',
                    {
                        text: tWhatsapp('freeToTemplateSubmit'),
                        onClick: () => {
                            updateSegmentedValue('template');
                        },
                    },
                    {text: 'cancelBtn'},
                );
                return;
            }
            updateSegmentedValue(next);
        },
        [persistedMode, hasFreeChanged],
    );

    const freeData = {
        disabledVariablesWarning: hasDisabledVariablesInFreeMessage(freeMessage, disabledVariables),
        totalCharacters: getWhatsAppPlainTextLength(freeMessage),
        filledItemsCount: 0,
        totalItemsCount: 0,
        isCharCountExceeded: getWhatsAppPlainTextLength(freeMessage) > WHATSAPP_MAX_TEXT_LENGTH,
        hasChanged: hasFreeChanged,
    };
    
    const templateTotalCharacters = calculateTotalCharacters(structure);
    const templateData = {
        disabledVariablesWarning: hasDisabledVariables(structure, disabledVariables),
        totalCharacters: templateTotalCharacters,
        filledItemsCount: getFilledItemsCount(structure),
        totalItemsCount: getTotalEditableItemsCount(structure),
        isCharCountExceeded: templateTotalCharacters > WHATSAPP_MAX_TEXT_LENGTH,
        hasChanged: hasTemplateChanged,
    };

    return {
        segmentedValue,
        handleSegmentedChange,
        templateButtonItems: structure?.filter((item): item is ButtonTemplateItem => item.type === 'button') ?? [],
        ...(persistedMode === 'free' ? freeData : templateData),
    };
}
